import {
  collection,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { enrichLeadWithAutoDistribution } from './leadAutoDistribution';
import { syncLeadToPipeline } from './syncLeadToPipeline';
import { trackGenerateLead } from '../services/analyticsService';
import { buildPropertyDataFromSearch } from './leadFormHelpers';
import { buildPropertyPath } from './seo/urlHelpers';

function parsePropertyPrice(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const s = String(raw)
    .trim()
    .replace(/[R$\s]/gi, '')
    .replace(/\./g, '')
    .replace(',', '.');
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

function resolvePropertyNegotiationValue(property) {
  if (!property) return null;
  const purpose = property.purpose || 'sale';
  const raw = purpose === 'rent'
    ? (property.rent ?? property.price ?? null)
    : (property.price ?? property.rent ?? null);
  return parsePropertyPrice(raw);
}

function updateStageHistory(currentStageHistory, newStatus) {
  const normalizeStatus = (status) => {
    if (!status) return 'lead';
    const statusLower = String(status).toLowerCase().trim();
    if (statusLower === 'novo' || statusLower === 'lead') return 'lead';
    if (statusLower === 'qualificado') return 'qualificado';
    if (statusLower === 'proposta' || statusLower === 'proposta enviada') return 'proposta';
    if (statusLower === 'negociacao' || statusLower === 'negociação') return 'negociacao';
    if (statusLower === 'ganho' || statusLower === 'convertido') return 'ganho';
    if (statusLower === 'perdido') return 'perdido';
    return statusLower;
  };

  const normalizedStatus = normalizeStatus(newStatus);
  const history = Array.isArray(currentStageHistory) ? [...currentStageHistory] : [];
  if (history.length === 0 || history[history.length - 1].stage !== normalizedStatus) {
    history.push({
      stage: normalizedStatus,
      timestamp: new Date()
    });
  }
  return history;
}

/**
 * Cria lead no CRM a partir da página de detalhes do imóvel.
 */
export async function createPropertyLead({
  name,
  phone,
  email,
  message,
  intent = 'info',
  visitDate,
  visitTime,
  property
}) {
  const propertyCode = property?.code ? String(property.code).trim() : '';
  const propertyId = property?.id || null;
  const propertyTitle = property?.title || '';
  const propertyUrl = propertyId
    ? `${window.location.origin}${buildPropertyPath(property)}`
    : '';

  const intentLabel = intent === 'visit' ? 'Agendar visita' : 'Mais informações';

  let descricao = (message || '').trim();
  if (!descricao) {
    descricao = intent === 'visit'
      ? `Gostaria de agendar uma visita para o imóvel ${propertyCode || propertyTitle}`
      : `Gostaria de mais informações sobre o imóvel ${propertyCode || propertyTitle}`;
  }

  const metaLines = [
    `Origem: Página do imóvel`,
    `Interesse: ${intentLabel}`,
    propertyCode ? `Cód. imóvel: ${propertyCode}` : null,
    propertyTitle ? `Imóvel: ${propertyTitle}` : null,
    propertyUrl ? `Link: ${propertyUrl}` : null,
    intent === 'visit' && visitDate ? `Data preferida: ${visitDate}` : null,
    intent === 'visit' && visitTime ? `Horário preferido: ${visitTime}` : null
  ].filter(Boolean);

  if (metaLines.length) {
    descricao = [descricao, '', '—', ...metaLines].filter(Boolean).join('\n');
  }

  const initialHistory = [
    {
      action: 'lead_criado',
      description: `Lead criado via página do imóvel (${intentLabel})`,
      performedBy: 'system',
      performedByName: 'Site',
      timestamp: new Date(),
      createdAt: new Date(),
      metadata: {
        source: 'property',
        intent,
        propertyId: propertyId || '',
        propertyCode: propertyCode || ''
      }
    }
  ];

  const initialStageHistory = updateStageHistory([], 'novo');
  const negotiationValue = resolvePropertyNegotiationValue(property);
  const linkedProperty = propertyCode
    ? {
        ...buildPropertyDataFromSearch({
          id: propertyId,
          code: propertyCode,
          title: propertyTitle,
          city: property?.city,
          neighborhood: property?.neighborhood,
          street: property?.street,
          number: property?.number,
          coverImage: property?.coverImage,
          images: property?.images,
          purpose: property?.purpose || 'sale',
          price: property?.price ?? null,
          rent: property?.rent ?? null,
          preco: property?.preco ?? null,
          valor: property?.valor ?? null,
          value: property?.value ?? null
        }),
        value: negotiationValue
      }
    : null;

  let leadData = {
    name: name.trim(),
    phone: phone.trim(),
    email: (email && email.trim()) || null,
    type: 'interest',
    status: 'novo',
    value: negotiationValue,
    propertyCode: linkedProperty ? [linkedProperty] : null,
    propertyId,
    propertyTitle,
    propertyLink: propertyUrl,
    descricao,
    adminNotes: '',
    source: 'property',
    intent,
    visitDate: intent === 'visit' ? (visitDate || null) : null,
    visitTime: intent === 'visit' ? (visitTime || null) : null,
    history: initialHistory,
    stageHistory: initialStageHistory,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  leadData = await enrichLeadWithAutoDistribution(db, leadData);

  try {
    const { submitPublicLeadViaFunction } = await import('../services/secureCallable');
    const { leadId } = await submitPublicLeadViaFunction({
      ...leadData,
      createdAt: undefined,
      updatedAt: undefined,
      message: leadData.descricao,
      notes: leadData.descricao,
    });
    trackGenerateLead({
      source: 'property',
      itemId: propertyId,
      itemType: 'property'
    });
    return leadId;
  } catch (err) {
    // Fallback se a Function ainda não estiver deployada / rede
    console.warn('submitPublicLead via Function falhou, fallback addDoc:', err?.message || err);
    const docRef = await addDoc(collection(db, 'leads'), leadData);
    await syncLeadToPipeline({ ...leadData, id: docRef.id }, docRef.id);
    trackGenerateLead({
      source: 'property',
      itemId: propertyId,
      itemType: 'property'
    });
    return docRef.id;
  }
}
